import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Actions, getSummaryStats } from "../../../actions/admin.action";
import Alert from "../../../components/alert";
import { SummaryStats } from "../../../dto";
import { ReactComponent as Loader } from "../../../images/loader.svg";
import dispatch, { ForbiddenError } from "../../../middleware";
import { RoutePaths } from "../../../models";
import { ReduxState } from "../../../reducers";
import { dateToDDMMYYYYHHMMAP, isLoadingActive } from "../../../utils";
import InfoCardList from "../infoCardList";
import { getSummarySection } from "../utils";
import { ReportSectionAuthStatus } from "../types";

interface SummaryProps {
  onAuthorizationChange: ReportSectionAuthStatus;
}

export default function Summary({ onAuthorizationChange }: SummaryProps) {
  const history = useHistory();
  const storeDispatch = useDispatch();
  const loadingQueue = useSelector(
    (state: ReduxState) => state.admin.loadingQueue,
  );
  const summaryStats: SummaryStats | null = useSelector(
    (state: ReduxState) => state.admin.summaryStats,
  );

  const [errorMessage, setErrorMessage] = useState("");
  const [notAuthorized, setNotAuthorized] = useState(false);

  const isLoading = isLoadingActive(loadingQueue, [
    Actions.REQUEST_GET_SUMMARY_STATS,
  ]);
  const fetchSummaryStats = useCallback(async () => {
    try {
      await dispatch(storeDispatch, getSummaryStats());
      onAuthorizationChange("summary", "authorized");
    } catch (error) {
      if (error instanceof ForbiddenError) {
        setNotAuthorized(true);
        onAuthorizationChange("summary", "forbidden");
      } else {
        setErrorMessage(
          "Your last request failed. Please reload the page again.",
        );
        onAuthorizationChange("summary", "error");
      }
    }
  }, [storeDispatch, onAuthorizationChange]);
  useEffect(() => {
    fetchSummaryStats();
  }, [fetchSummaryStats]);

  if (isLoading) {
    return (
      <div className="d-flex align-items-center justify-content-center dashboard-container p-3">
        <Loader />
      </div>
    );
  }

  if (notAuthorized)
    return (
      <div className="dashboard-container">
        <Alert type="danger" message="Not authorized to view summary." />
      </div>
    );

  if (!summaryStats)
    return (
      <div className="d-flex align-items-baseline justify-content-center">
        <p className="text-muted">
          Unable to load summary stats. Please reload the page again.
        </p>
      </div>
    );
  const summarySection = getSummarySection(summaryStats);
  const { sectionName, infoCards, lastUpdateAt } = summarySection;

  function handleCardNavigateClick(routePath?: RoutePaths) {
    if (!routePath) return;
    history.push(routePath);
  }

  return (
    <div className="dashboard-container">
      <Alert
        message={errorMessage}
        type="info"
        canDismiss
        onDismissClick={() => setErrorMessage("")}
      />

      <section>
        <div className="d-flex align-items-center justify-content-between">
          <h3 className="user-page-title">{sectionName}</h3>
          <span className="small">
            Last Updated At: {dateToDDMMYYYYHHMMAP(lastUpdateAt)}
          </span>
        </div>

        <InfoCardList
          infoCards={infoCards}
          onCardClick={handleCardNavigateClick}
        />
      </section>
    </div>
  );
}
